import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Player from "@vimeo/player";
import { ResourceDto } from "../types/resources";
import { CourseEventType, recordCourseEvent, VideoAnalytics } from "../services/courseEventService";
import "../styles/resources.css";

type SimpleVideo = {
    id: number;
    title: string;
    vimeoUrl: string;
};

type Props = {
    open: boolean;
    resource?: ResourceDto | SimpleVideo | null;
    onClose: () => void;
    /** Called when video playback ends */
    onVideoEnd?: (resourceId: number) => void;
    /** When provided, VIDEO_OPEN and VIDEO_COMPLETE events are recorded for Course Insights. */
    analytics?: VideoAnalytics | null;
};

// Type guard to check if resource is ResourceDto
function isResourceDto(resource: ResourceDto | SimpleVideo): resource is ResourceDto {
    return 'launchModalUrl' in resource;
}

type VideoProvider = 'vimeo' | 'wistia' | 'youtube' | 'generic';

interface VideoInfo {
    provider: VideoProvider;
    id?: string;
    embedUrl: string;
}

/**
 * YouTube requires the embedding page origin on the embed URL for many contexts (incl. localhost);
 * without it, the player often shows a generic "An error occurred" / Error 153.
 */
function buildYoutubeEmbedUrl(videoId: string): string {
    const params = new URLSearchParams({
        autoplay: "1",
        rel: "0",
        playsinline: "1",
        modestbranding: "1",
    });
    if (typeof window !== "undefined" && window.location?.origin) {
        params.set("origin", window.location.origin);
    }
    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

/** Vimeo embed params that strip branding and discourage leaving the app. */
function buildVimeoEmbedUrl(videoId: string): string {
    const params = new URLSearchParams({
        autoplay: "1",
        title: "0",
        byline: "0",
        portrait: "0",
        badge: "0",
        vimeo_logo: "0",
        dnt: "1",
    });
    return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
}

function parseVideoUrl(url?: string | null): VideoInfo | null {
    if (!url) return null;
    const trimmed = url.trim();

    // Check for Vimeo
    if (trimmed.includes('vimeo.com')) {
        // Handle direct video ID
        if (/^\d+$/.test(trimmed)) {
            return {
                provider: 'vimeo',
                id: trimmed,
                embedUrl: buildVimeoEmbedUrl(trimmed)
            };
        }
        // Extract from URL
        const vimeoMatch = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
        if (vimeoMatch?.[1]) {
            return {
                provider: 'vimeo',
                id: vimeoMatch[1],
                embedUrl: buildVimeoEmbedUrl(vimeoMatch[1])
            };
        }
    }

    // Check for Wistia
    if (trimmed.includes('wistia.com')) {
        const wistiaMatch = trimmed.match(/wistia\.com\/medias\/([a-zA-Z0-9]+)/i);
        if (wistiaMatch?.[1]) {
            return {
                provider: 'wistia',
                id: wistiaMatch[1],
                // Enable postMessage API with videoFoam=true parameter
                embedUrl: `https://fast.wistia.net/embed/iframe/${wistiaMatch[1]}?autoplay=1&videoFoam=true`
            };
        }
    }

    // Check for YouTube (watch, Shorts, embed, youtu.be — never use <video src> for page URLs)
    if (trimmed.includes('youtube.com') || trimmed.includes('youtu.be')) {
        let youtubeId: string | undefined;
        // youtu.be/VIDEO_ID
        const youtuBeMatch = trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
        if (youtuBeMatch?.[1]) {
            youtubeId = youtuBeMatch[1];
        }
        // youtube.com/watch?v=VIDEO_ID
        if (!youtubeId) {
            const watchMatch = trimmed.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
            if (watchMatch?.[1]) {
                youtubeId = watchMatch[1];
            }
        }
        // youtube.com/shorts/VIDEO_ID (Shorts use the same 11-char id as embed)
        if (!youtubeId) {
            const shortsMatch = trimmed.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/i);
            if (shortsMatch?.[1]) {
                youtubeId = shortsMatch[1];
            }
        }
        // youtube.com/embed/VIDEO_ID (already an embed path)
        if (!youtubeId) {
            const embedPathMatch = trimmed.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/i);
            if (embedPathMatch?.[1]) {
                youtubeId = embedPathMatch[1];
            }
        }
        // youtube.com/live/VIDEO_ID
        if (!youtubeId) {
            const liveMatch = trimmed.match(/youtube\.com\/live\/([a-zA-Z0-9_-]{11})/i);
            if (liveMatch?.[1]) {
                youtubeId = liveMatch[1];
            }
        }
        if (youtubeId) {
            return {
                provider: 'youtube',
                id: youtubeId,
                embedUrl: buildYoutubeEmbedUrl(youtubeId),
            };
        }
    }

    // Fallback to generic video player
    return {
        provider: 'generic',
        embedUrl: trimmed
    };
}

export default function VideoLightbox({ open, resource, onClose, onVideoEnd, analytics }: Props){
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const analyticsRef = useRef(analytics);
    analyticsRef.current = analytics;
    const resourceRef = useRef(resource);
    resourceRef.current = resource;

    const emit = useCallback((eventType: CourseEventType) => {
        const context = analyticsRef.current;
        if (!context) return;
        recordCourseEvent({ ...context, eventType });
    }, []);

    useEffect(() => {
        if (open && resourceRef.current && analyticsRef.current) {
            emit("VIDEO_OPEN");
        }
    }, [open, emit]);

    useEffect(() => {
        if (!open || !resource) return;

        // Get the video URL based on resource type
        const videoUrl = isResourceDto(resource) ? resource.launchModalUrl : resource.vimeoUrl;
        const videoInfo = parseVideoUrl(videoUrl);

        // Only initialize Vimeo player for Vimeo videos
        if (videoInfo?.provider === 'vimeo' && iframeRef.current) {

            // Small delay to ensure iframe is loaded
            const initTimer = setTimeout(() => {
                if (!iframeRef.current) {
                    return;
                }

                const player = new Player(iframeRef.current);

                // When ready, set up event listeners
                player.ready()
                    .then(() => {
                        // Video is ready
                    })
                    .catch((_err) => {
                        // Silently handle errors
                    });

                // Listen for video end event
                const handleEnded = () => {
                    emit("VIDEO_COMPLETE");
                    if (onVideoEnd) {
                        onVideoEnd(resource.id);
                    }
                };

                player.on('ended', handleEnded);

                // ESC to close
                const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
                window.addEventListener("keydown", onKey);

                // Store cleanup function
                (window as any).__vimeoCleanup = () => {
                    window.removeEventListener("keydown", onKey);
                    player.off('ended', handleEnded);
                    player.destroy().catch(() => {});
                };
            }, 100);

            return () => {
                clearTimeout(initTimer);
                if ((window as any).__vimeoCleanup) {
                    (window as any).__vimeoCleanup();
                    delete (window as any).__vimeoCleanup;
                }
            };
        }

        // For Wistia videos, use Wistia Player API
        if (videoInfo?.provider === 'wistia' && videoInfo.id) {

            // Load Wistia API script if not already loaded
            if (!(window as any)._wq) {
                (window as any)._wq = (window as any)._wq || [];
            }

            // Wait for Wistia API to be available and bind to the video
            const wistiaId = videoInfo.id;
            let cleanupDone = false;

            const bindWistiaVideo = () => {
                (window as any)._wq = (window as any)._wq || [];
                (window as any)._wq.push({
                    id: wistiaId,
                    onReady: function(video: any) {
                        // Bind to end event
                        video.bind('end', function() {
                            if (!cleanupDone) {
                                emit("VIDEO_COMPLETE");
                                if (onVideoEnd) {
                                    onVideoEnd(resource.id);
                                }
                            }
                        });
                    }
                });
            };

            // Load Wistia embed script if not present
            if (!document.querySelector('script[src*="wistia.com/assets/external/E-v1.js"]')) {
                const script = document.createElement('script');
                script.src = 'https://fast.wistia.com/assets/external/E-v1.js';
                script.async = true;
                script.onload = () => {
                    setTimeout(bindWistiaVideo, 500);
                };
                document.body.appendChild(script);
            } else {
                setTimeout(bindWistiaVideo, 500);
            }

            const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
            window.addEventListener("keydown", onKey);

            return () => {
                cleanupDone = true;
                window.removeEventListener("keydown", onKey);
            };
        }

        // For generic HTML5 videos
        if (videoInfo?.provider === 'generic' && videoRef.current) {
            const videoElement = videoRef.current;
            const handleVideoEnd = () => {
                emit("VIDEO_COMPLETE");
                if (onVideoEnd) {
                    onVideoEnd(resource.id);
                }
            };
            videoElement.addEventListener('ended', handleVideoEnd);

            const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
            window.addEventListener("keydown", onKey);
            return () => {
                window.removeEventListener("keydown", onKey);
                videoElement.removeEventListener('ended', handleVideoEnd);
            };
        }

        // For YouTube videos, listen for ENDED via IFrame API when available
        if (videoInfo?.provider === 'youtube' && videoInfo.id) {
            const youtubeId = videoInfo.id;
            let cleanupDone = false;

            const onYouTubeEnded = () => {
                if (cleanupDone) return;
                emit("VIDEO_COMPLETE");
                if (onVideoEnd && resource) {
                    onVideoEnd(resource.id);
                }
            };

            const bindYouTubePlayer = () => {
                const YT = (window as any).YT;
                if (!YT?.Player) {
                    return;
                }
                new YT.Player(iframeRef.current, {
                    events: {
                        onStateChange: (event: { data: number }) => {
                            if (event.data === YT.PlayerState.ENDED) {
                                onYouTubeEnded();
                            }
                        },
                    },
                });
            };

            if (!(window as any).YT?.Player) {
                const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
                if (!existingScript) {
                    const tag = document.createElement('script');
                    tag.src = 'https://www.youtube.com/iframe_api';
                    document.body.appendChild(tag);
                }
                const previousReady = (window as any).onYouTubeIframeAPIReady;
                (window as any).onYouTubeIframeAPIReady = () => {
                    previousReady?.();
                    bindYouTubePlayer();
                };
            } else {
                bindYouTubePlayer();
            }

            const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
            window.addEventListener("keydown", onKey);
            return () => {
                cleanupDone = true;
                window.removeEventListener("keydown", onKey);
            };
        }

        // Fallback: just handle ESC key
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => { window.removeEventListener("keydown", onKey); };
    }, [open, resource, onClose, onVideoEnd, emit]);

    if (!open || !resource) {
        return null;
    }

    // Get the video URL based on resource type
    const videoUrl = isResourceDto(resource) ? resource.launchModalUrl : resource.vimeoUrl;
    const videoInfo = parseVideoUrl(videoUrl);

    if (!videoInfo) {
        return null;
    }

    return createPortal(
        <div className="vimeo-lightbox" role="dialog" aria-modal="true">
            <div className="vimeo-lightbox-card">
                <button className="vimeo-close" aria-label="Close" onClick={onClose}>×</button>
                {videoInfo.provider === 'vimeo' ? (
                    <div className="vimeo-embed-wrap">
                        <iframe
                            ref={iframeRef}
                            src={videoInfo.embedUrl}
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={resource.title}
                        />
                        <div className="vimeo-branding-shield" aria-hidden="true" />
                    </div>
                ) : videoInfo.provider === 'youtube' ? (
                    <iframe
                        ref={iframeRef}
                        src={videoInfo.embedUrl}
                        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                        allowFullScreen
                        title={resource.title}
                    />
                ) : videoInfo.provider === 'wistia' && videoInfo.id ? (
                    <div
                        className={`wistia_embed wistia_async_${videoInfo.id} videoFoam=true`}
                        style={{ width: '100%', height: '100%', position: 'relative' }}
                    >
                        &nbsp;
                    </div>
                ) : (
                    <video
                        ref={videoRef}
                        src={videoInfo.embedUrl}
                        controls
                        autoPlay
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    >
                        Your browser does not support the video tag.
                    </video>
                )}
            </div>
        </div>,
        document.body
    );
}